import createClient from "openapi-fetch";
import { formData } from "./openapi.js";
import { USER_AGENT } from "./consts.js";
/**
 * The OpenFoodFacts main API client for version 2.
 *
 * You should not use this class directly, instead use the `OpenFoodFactsApi` class.
 */
export class ProductOpenerApiV2 {
    constructor(fetch, options) {
        this.fetch = fetch;
        this.baseUrl = options.host;
        this.client = createClient({
            fetch: this.fetch,
            baseUrl: this.baseUrl,
        });
    }
    /**
     * Get a taxonomy entry by its type and ID.
     * @param taxo - The type of taxonomy (e.g., "categories", "brands").
     * @param entry - The ID of the taxonomy entry.
     */
    async getTaxoEntry(taxo, entry) {
        const res = await this.fetch(`${this.baseUrl}/api/v2/taxonomy?tagtype=${taxo}&tags=${entry}`);
        return (await res.json());
    }
    /**
     * Performs OCR on a product image.
     * @example const {data, error} = await performOCR(barcode, photoId, ocrEngine);
     */
    async performOCR(barcode, photoId, ocrEngine = "google_cloud_vision") {
        return this.client.GET("/cgi/ingredients.pl", {
            params: {
                query: {
                    code: barcode,
                    id: photoId,
                    ocr_engine: ocrEngine,
                    process_image: "1",
                },
            },
        });
    }
    /**
     * @example const {data, error} = await search(query);
     */
    async search(query) {
        return this.client.GET("/api/v2/search", {
            params: { query },
        });
    }
    /**
     * Returns all available attribute groups
     * @returns A promise that resolves to an array of attribute groups
     * @example
     * const {data, error} = await getAttributeGroups();
     */
    async getAttributeGroups() {
        return this.client.GET("/api/v2/attribute_groups");
    }
    /**
     * Unselects an image for a product
     * @param barcode - The barcode of the product
     * @param id - Image field (image id) of the photo to unselect (e.g., "front_fr")
     * @returns A promise that resolves to the unselect response
     */
    async unselectImage(barcode, id) {
        return this.client.POST("/cgi/product_image_unselect.pl", {
            body: { code: barcode, id: id },
        });
    }
    /**
     * Returns product attributes for a given barcode
     * @param barcode - The barcode of the product
     * @returns A promise that resolves to an array of product attributes
     */
    async getProductAttributes(barcode) {
        var _a, _b;
        const res = await this.client.GET("/api/v2/product/{barcode}", {
            params: {
                path: { barcode },
                query: { fields: "product_name,code,attribute_groups_en" },
            },
        });
        // @ts-expect-error - OpenAPI schema may not include all possible fields
        return ((_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.product) === null || _b === void 0 ? void 0 : _b.attribute_groups_en) || [];
    }
    /**
     * Adds or edits a product using the V2 API
     * @param product - The product data to add or edit
     * @param credentials - Optional credentials for authentication
     * @returns A promise that resolves to true if successful, false otherwise
     */
    async addOrEditProductV2(product, credentials) {
        var _a;
        const url = `${this.baseUrl}/cgi/product_jqm2.pl`;
        const languageCodes = Object.keys(product.languages_codes || {});
        const productNames = languageCodes.reduce((acc, lang) => {
            const productName = getProductNameInLang(product, lang);
            if (productName != null) {
                acc[`product_name_${lang}`] = productName;
            }
            return acc;
        }, {});
        const ingredientsTexts = languageCodes.reduce((acc, lang) => {
            const ingredientsText = getProductIngredientsInLang(product, lang);
            if (ingredientsText != null) {
                acc[`ingredients_text_${lang}`] = ingredientsText;
            }
            return acc;
        }, {});
        const body = formData(Object.assign(Object.assign({ code: product.code, user_id: credentials === null || credentials === void 0 ? void 0 : credentials.username, password: credentials === null || credentials === void 0 ? void 0 : credentials.password, categories: product.categories || "", labels: product.labels || "", brands: product.brands || "", quantity: product.quantity || "", serving_size: product.serving_size || "", stores: product.stores || "", origins: product.origins || "", countries: product.countries || "", emb_codes: product.emb_codes || "", packaging: product.packaging || "", manufacturing_places: product.manufacturing_places || "", comment: (_a = product.comment) !== null && _a !== void 0 ? _a : "", product_name: product.product_name || "", ingredients_text: product.ingredients_text || "" }, productNames), ingredientsTexts));
        const res = await this.fetch(url, {
            method: "POST",
            body,
            headers: {
                "User-Agent": USER_AGENT,
            },
        });
        return res.status === 200;
    }
    /**
     * Crops and selects an image for a product
     * @param barcode - The barcode of the product
     * @param imgid - Identifier of the image to select (should be a number)
     * @param id - Identifier of the selected image field (format: {IMAGE_TYPE}_{LANG})
     * @param cropData - Crop coordinates and options
     * @returns A promise that resolves to the crop response
     *
     * @example
     * const {data, error} = await cropImage(barcode, imgid, id, cropData);
     */
    async cropImage(barcode, imgid, id, cropData) {
        return this.client.POST("/cgi/product_image_crop.pl", {
            body: {
                code: barcode,
                imgid: imgid,
                id: id,
                x1: cropData.x1,
                y1: cropData.y1,
                x2: cropData.x2,
                y2: cropData.y2,
                angle: cropData.angle,
                normalize: cropData.normalize ? "true" : "false",
                white_magic: cropData.white_magic ? "true" : "false",
                comment: cropData.comment,
                app_name: cropData.app_name,
                app_version: cropData.app_version,
                app_uuid: cropData.app_uuid,
                "User-Agent": cropData.user_agent,
            },
        });
    }
    /**
     * Uploads an image to OpenFoodFacts for a product.
     * @param barcode - The barcode of the product
     * @param imageFile - The image file to upload
     * @param imagefield - The field name for the image (e.g., "front", "ingredients", "nutrition")
     * @returns A promise that resolves to the upload response
     */
    async uploadImage(barcode, imageFile, imagefield) {
        return this.client.POST("/cgi/product_image_upload.pl", {
            // @ts-expect-error - OpenAPI schema wrong
            body: {
                code: barcode,
                imagefield: imagefield,
                [`imgupload_${imagefield}`]: imageFile,
                "User-Agent": USER_AGENT,
            },
            bodySerializer: formData,
        });
    }
    /**
     * Rotates an image for a product
     * @param barcode - The barcode of the product
     * @param id - Identifier of the selected image field (format: {IMAGE_TYPE}_{LANG})
     * @param imgid - Identifier of the image to rotate (should be a number as string)
     * @param angle - Angle of rotation in degrees (90, 180, or 270 clockwise)
     * @returns A promise that resolves to the rotation response
     */
    async rotateImage(barcode, id, imgid, angle) {
        return await this.client.GET("/cgi/product_image_crop.pl", {
            params: {
                query: {
                    code: barcode,
                    id: id,
                    imgid: imgid,
                    angle: angle,
                },
            },
        });
    }
    /**
     * Returns product data using the V2 API
     * @param barcode - The barcode of the product
     * @returns A promise that resolves to the product data or undefined if not found
     * @example
     * const {data, error} = await getProductV2(barcode);
     */
    async getProductV2(barcode) {
        return this.client.GET("/api/v2/product/{barcode}", {
            params: { path: { barcode } },
        });
    }
    /**
     * Returns an array of image names for the product
     * @param barcode - The barcode of the product
     * @returns A promise that resolves to an array of image names or null if not found
     */
    async getProductImages(barcode) {
        var _a, _b;
        const res = await this.client.GET("/api/v2/product/{barcode}", {
            params: {
                query: { fields: "images" },
                path: { barcode },
            },
        });
        const product = (_a = res.data) === null || _a === void 0 ? void 0 : _a.product;
        // Check if the returned type has images
        if (!product)
            return null;
        if (!("images" in product))
            return null;
        const images = (_b = product.images) !== null && _b !== void 0 ? _b : {};
        return Object.keys(images);
    }
}
export function getProductNameInLang(product, lang) {
    var _a;
    return (_a = product[`product_name_${lang}`]) !== null && _a !== void 0 ? _a : product.product_name;
}
export function getProductIngredientsInLang(product, lang) {
    var _a;
    return (_a = product[`ingredients_text_${lang}`]) !== null && _a !== void 0 ? _a : product.ingredients_text;
}
//# sourceMappingURL=off-v2.js.map